/**
 * Leading at Gallo Dashboard
 * Main dashboard after authentication - determines user role and redirects
 */

import React, { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import RoleToggle from './RoleToggle';

const LeadingDashboard = () => {
  const { user, logout, userRole } = useAuth();
  const navigate = useNavigate();

  // Check if user is admin and redirect to appropriate dashboard
  useEffect(() => {
    if (user) {
      // Check role from context
      const isAdmin = userRole === 'admin';
      
      if (isAdmin) {
        // Redirect to admin cohorts page
        navigate('/leading/admin/cohorts', { replace: true });
      } else {
        // Redirect to participant surveys page
        navigate('/leading/my-surveys', { replace: true });
      }
    }
  }, [user, userRole, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/leading/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="assessment-details-container">
      <RoleToggle />
      <div className="assessment-header">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0 }}>Leading at Gallo Dashboard</h1>
            <button 
              onClick={handleLogout}
              className="questionnaire-button"
              style={{ background: '#6b7280' }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
      
      <div className="assessment-content">
        <div className="container">
          {/* Welcome Card */}
          <div className="stats-card" style={{ marginBottom: '2rem' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1.5rem',
              padding: '1rem'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '2rem',
                fontWeight: '700'
              }}>
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem' }}>
                  Welcome, {user?.name || 'User'}!
                </h2>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '1rem' }}>
                  {user?.email}
                </p>
                <div style={{ 
                  marginTop: '0.5rem',
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  background: '#dbeafe',
                  color: '#1e40af',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}>
                  {user?.roles?.includes('Admin') ? 'Administrator' : 'Participant'}
                </div>
              </div>
            </div>
          </div>

          {/* Success Message */}
          <div className="stats-card" style={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            marginBottom: '2rem'
          }}>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 0.5rem 0' }}>
                Authentication Successful!
              </h3>
              <p style={{ margin: 0, opacity: 0.9 }}>
                You have successfully signed in with your Microsoft account.
              </p>
            </div>
          </div>

          {/* User Information */}
          <div className="stats-card">
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              Your Profile Information
            </h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ 
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontWeight: '600', color: '#6b7280' }}>Name:</span>
                <span style={{ color: '#111827' }}>{user?.name || 'N/A'}</span>
              </div>
              <div style={{ 
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontWeight: '600', color: '#6b7280' }}>Email:</span>
                <span style={{ color: '#111827' }}>{user?.email || 'N/A'}</span>
              </div>
              <div style={{ 
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontWeight: '600', color: '#6b7280' }}>User ID:</span>
                <span style={{ color: '#111827', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {user?.userId || 'N/A'}
                </span>
              </div>
              <div style={{ 
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontWeight: '600', color: '#6b7280' }}>Roles:</span>
                <span style={{ color: '#111827' }}>
                  {user?.roles?.length > 0 ? user.roles.join(', ') : 'Participant'}
                </span>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="stats-card" style={{ marginTop: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem' }}>
              Next Steps
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
              The Leading at Gallo platform is currently under development. Here's what's coming:
            </p>
            <ul style={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0,
              display: 'grid',
              gap: '0.75rem'
            }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%',
                  background: '#dbeafe',
                  color: '#1e40af',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '700'
                }}>1</span>
                <span>360-degree feedback surveys</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%',
                  background: '#dbeafe',
                  color: '#1e40af',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '700'
                }}>2</span>
                <span>Leadership competency assessments</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%',
                  background: '#dbeafe',
                  color: '#1e40af',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '700'
                }}>3</span>
                <span>Analytics and reporting dashboard</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%',
                  background: '#dbeafe',
                  color: '#1e40af',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '700'
                }}>4</span>
                <span>Cohort and participant management</span>
              </li>
            </ul>
          </div>

          {/* Temporary Admin Access */}
          <div className="stats-card" style={{ marginTop: '2rem', background: '#fef3c7', border: '1px solid #fde68a' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: '#92400e' }}>
              🔧 Development Access
            </h3>
            <p style={{ color: '#78350f', marginBottom: '1.5rem' }}>
              Since the admin role assignment system isn't connected yet, you can access the admin features directly:
            </p>
            <button 
              onClick={() => navigate('/leading/admin/cohorts')}
              className="questionnaire-button"
              style={{ 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                width: '100%',
                marginBottom: '0.75rem'
              }}
            >
              Access Admin Dashboard
            </button>
          </div>

          {/* Actions */}
          <div style={{ 
            marginTop: '2rem', 
            display: 'flex', 
            gap: '1rem',
            justifyContent: 'center'
          }}>
            <a 
              href="/"
              className="questionnaire-button"
              style={{ background: '#6b7280' }}
            >
              Back to Homepage
            </a>
            <button 
              onClick={handleLogout}
              className="questionnaire-button questionnaire-button-primary"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadingDashboard;
